import { jsx, jsxs } from 'react/jsx-runtime';
import { memo, useRef, useEffect } from 'react';
import cc from 'classcat';
import shallow from 'zustand/shallow';
import { zoom, zoomIdentity } from 'd3-zoom';
import { select, pointer } from 'd3-selection';
import { useStoreApi, useStore, Panel, getBoundsOfRects, getRectOfNodes } from '@reactflow/core';

var MiniMapNode = function MiniMapNode(_ref) {
  var id = _ref.id,
      x = _ref.x,
      y = _ref.y,
      width = _ref.width,
      height = _ref.height,
      style = _ref.style,
      color = _ref.color,
      strokeColor = _ref.strokeColor,
      strokeWidth = _ref.strokeWidth,
      className = _ref.className,
      borderRadius = _ref.borderRadius,
      shapeRendering = _ref.shapeRendering,
      onClick = _ref.onClick;

  var _ref2 = style || {},
      background = _ref2.background,
      backgroundColor = _ref2.backgroundColor;

  var fill = color || background || backgroundColor;
  return jsx("rect", {
    className: cc(['react-flow__minimap-node', className]),
    x: x,
    y: y,
    rx: borderRadius,
    ry: borderRadius,
    width: width,
    height: height,
    fill: fill,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    shapeRendering: shapeRendering,
    onClick: onClick ? function (event) {
      return onClick(event, id);
    } : undefined
  });
};

MiniMapNode.displayName = 'MiniMapNode';
var MiniMapNode$1 = memo(MiniMapNode);
var defaultWidth = 200;
var defaultHeight = 150;

var selector = function selector(s) {
  var nodes = Array.from(s.nodeInternals.values());
  var viewBB = {
    x: -s.transform[0] / s.transform[2],
    y: -s.transform[1] / s.transform[2],
    width: s.width / s.transform[2],
    height: s.height / s.transform[2]
  };
  return {
    nodes: nodes.filter(function (node) {
      return !node.hidden && node.width && node.height;
    }),
    viewBB: viewBB,
    boundingRect: nodes.length > 0 ? getBoundsOfRects(getRectOfNodes(nodes), viewBB) : viewBB,
    rfId: s.rfId
  };
};

var getAttrFunction = function getAttrFunction(func) {
  return func instanceof Function ? func : function () {
    return func;
  };
};

var ARIA_LABEL_KEY = 'react-flow__minimap-desc';

function MiniMap(_ref3) {
  var _style$width, _style$height;

  var style = _ref3.style,
      className = _ref3.className,
      _ref3$nodeStrokeColor = _ref3.nodeStrokeColor,
      nodeStrokeColor = _ref3$nodeStrokeColor === void 0 ? 'transparent' : _ref3$nodeStrokeColor,
      _ref3$nodeColor = _ref3.nodeColor,
      nodeColor = _ref3$nodeColor === void 0 ? '#e2e2e2' : _ref3$nodeColor,
      _ref3$nodeClassName = _ref3.nodeClassName,
      nodeClassName = _ref3$nodeClassName === void 0 ? '' : _ref3$nodeClassName,
      _ref3$nodeBorderRadiu = _ref3.nodeBorderRadius,
      nodeBorderRadius = _ref3$nodeBorderRadiu === void 0 ? 5 : _ref3$nodeBorderRadiu,
      _ref3$nodeStrokeWidth = _ref3.nodeStrokeWidth,
      nodeStrokeWidth = _ref3$nodeStrokeWidth === void 0 ? 2 : _ref3$nodeStrokeWidth,
      _ref3$maskColor = _ref3.maskColor,
      maskColor = _ref3$maskColor === void 0 ? 'rgb(240, 240, 240, 0.6)' : _ref3$maskColor,
      _ref3$position = _ref3.position,
      position = _ref3$position === void 0 ? 'bottom-right' : _ref3$position,
      onClick = _ref3.onClick,
      onNodeClick = _ref3.onNodeClick,
      _ref3$pannable = _ref3.pannable,
      pannable = _ref3$pannable === void 0 ? false : _ref3$pannable,
      _ref3$zoomable = _ref3.zoomable,
      zoomable = _ref3$zoomable === void 0 ? false : _ref3$zoomable;
  var store = useStoreApi();
  var svg = useRef(null);

  var _useStore = useStore(selector, shallow),
      boundingRect = _useStore.boundingRect,
      viewBB = _useStore.viewBB,
      nodes = _useStore.nodes,
      rfId = _useStore.rfId;

  var elementWidth = (_style$width = style === null || style === void 0 ? void 0 : style.width) !== null && _style$width !== void 0 ? _style$width : defaultWidth;
  var elementHeight = (_style$height = style === null || style === void 0 ? void 0 : style.height) !== null && _style$height !== void 0 ? _style$height : defaultHeight;
  var nodeColorFunc = getAttrFunction(nodeColor);
  var nodeStrokeColorFunc = getAttrFunction(nodeStrokeColor);
  var nodeClassNameFunc = getAttrFunction(nodeClassName);
  var scaledWidth = boundingRect.width / elementWidth;
  var scaledHeight = boundingRect.height / elementHeight;
  var viewScale = Math.max(scaledWidth, scaledHeight);
  var viewWidth = viewScale * elementWidth;
  var viewHeight = viewScale * elementHeight;
  var offset = 5 * viewScale;
  var x = boundingRect.x - (viewWidth - boundingRect.width) / 2 - offset;
  var y = boundingRect.y - (viewHeight - boundingRect.height) / 2 - offset;
  var width = viewWidth + offset * 2;
  var height = viewHeight + offset * 2;
  var shapeRendering = typeof window === 'undefined' || !!window.chrome ? 'crispEdges' : 'geometricPrecision';
  var labelledBy = "".concat(ARIA_LABEL_KEY, "-").concat(rfId);
  var viewScaleRef = useRef(0);
  viewScaleRef.current = viewScale;
  useEffect(function () {
    if (svg.current) {
      var selection = select(svg.current);

      var zoomHandler = function zoomHandler(event) {
        var _store$getState = store.getState(),
            transform = _store$getState.transform,
            d3Selection = _store$getState.d3Selection,
            d3Zoom = _store$getState.d3Zoom;

        if (event.sourceEvent.type !== 'wheel' || !d3Selection || !d3Zoom) {
          return;
        }

        var pinchDelta = -event.sourceEvent.deltaY * (event.sourceEvent.deltaMode === 1 ? 0.05 : event.sourceEvent.deltaMode ? 1 : 0.002) * 10;
        var zoom = transform[2] * Math.pow(2, pinchDelta);
        d3Zoom.scaleTo(d3Selection, zoom);
      };

      var panHandler = function panHandler(event) {
        var _store$getState2 = store.getState(),
            transform = _store$getState2.transform,
            d3Selection = _store$getState2.d3Selection,
            d3Zoom = _store$getState2.d3Zoom;

        if (event.sourceEvent.type !== 'mousemove' || !d3Selection || !d3Zoom) {
          return;
        } // @TODO: how to calculate the correct next position? Math.max(1, transform[2]) is a workaround.


        var position = {
          x: transform[0] - event.sourceEvent.movementX * viewScaleRef.current * Math.max(1, transform[2]),
          y: transform[1] - event.sourceEvent.movementY * viewScaleRef.current * Math.max(1, transform[2])
        };
        var nextTransform = zoomIdentity.translate(position.x, position.y).scale(transform[2]);
        d3Zoom.transform(d3Selection, nextTransform);
      };

      var zoomAndPanHandler = zoom() // @ts-ignore
      .on('zoom', pannable ? panHandler : null) // @ts-ignore
      .on('zoom.wheel', zoomable ? zoomHandler : null);
      selection.call(zoomAndPanHandler);
      return function () {
        selection.on('zoom', null);
      };
    }
  }, [pannable, zoomable]);
  var onSvgClick = onClick ? function (event) {
    var rfCoord = pointer(event);
    onClick(event, {
      x: rfCoord[0],
      y: rfCoord[1]
    });
  } : undefined;
  var onSvgNodeClick = onNodeClick ? function (event, nodeId) {
    var node = store.getState().nodeInternals.get(nodeId);
    onNodeClick(event, node);
  } : undefined;
  return jsx(Panel, {
    position: position,
    style: style,
    className: cc(['react-flow__minimap', className]),
    children: jsxs("svg", {
      width: elementWidth,
      height: elementHeight,
      viewBox: "".concat(x, " ").concat(y, " ").concat(width, " ").concat(height),
      role: "img",
      "aria-labelledby": labelledBy,
      ref: svg,
      onClick: onSvgClick,
      children: [jsx("title", {
        id: labelledBy,
        children: "React Flow mini map"
      }), nodes.map(function (node) {
        var _node$positionAbsolut, _node$positionAbsolut2, _node$positionAbsolut3, _node$positionAbsolut4;

        return jsx(MiniMapNode$1, {
          x: (_node$positionAbsolut = (_node$positionAbsolut2 = node.positionAbsolute) === null || _node$positionAbsolut2 === void 0 ? void 0 : _node$positionAbsolut2.x) !== null && _node$positionAbsolut !== void 0 ? _node$positionAbsolut : 0,
          y: (_node$positionAbsolut3 = (_node$positionAbsolut4 = node.positionAbsolute) === null || _node$positionAbsolut4 === void 0 ? void 0 : _node$positionAbsolut4.y) !== null && _node$positionAbsolut3 !== void 0 ? _node$positionAbsolut3 : 0,
          width: node.width,
          height: node.height,
          style: node.style,
          className: nodeClassNameFunc(node),
          color: nodeColorFunc(node),
          borderRadius: nodeBorderRadius,
          strokeColor: nodeStrokeColorFunc(node),
          strokeWidth: nodeStrokeWidth,
          shapeRendering: shapeRendering,
          onClick: onSvgNodeClick,
          id: node.id
        }, node.id);
      }), jsx("path", {
        className: "react-flow__minimap-mask",
        d: "M".concat(x - offset, ",").concat(y - offset, "h").concat(width + offset * 2, "v").concat(height + offset * 2, "h").concat(-width - offset * 2, "z\n        M").concat(viewBB.x, ",").concat(viewBB.y, "h").concat(viewBB.width, "v").concat(viewBB.height, "h").concat(-viewBB.width, "z"),
        fill: maskColor,
        fillRule: "evenodd"
      })]
    })
  });
}

MiniMap.displayName = 'MiniMap';
var MiniMap$1 = memo(MiniMap);
export { MiniMap$1 as MiniMap };